import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsis, faPenToSquare, faCircleMinus, faPlus, faArrowDownShortWide, faArrowDownWideShort, faSearch } from "@fortawesome/pro-regular-svg-icons";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { getAllDiscounts, deleteDiscount } from "../../api/Discounts";
import { useAuth } from "../../providers/AuthContext";
import CustomDropdown from "../../components/CustomDropdown";
import InputField from "../../components/InputField";  
import toast from 'react-hot-toast';
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import { format } from 'date-fns';
import { useTranslation } from "react-i18next";

const DiscountsPage = () => {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth();
  const [discounts, setDiscounts] = useState([]);
  const [filteredDiscounts, setFilteredDiscounts] = useState([]);
  const [sortColumn, setSortColumn] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [search, setSearch] = useState('');
  const [confirmState, setConfirmState] = useState({ isOpen: false, title: "", confirm: () => { } });
  const navigate = useNavigate();
  const [discountsLoaded, setDiscountsLoaded] = useState(false);

  useEffect(() => {
    handleGetDiscounts();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, []);

  useEffect(() => {
    const applyFilterAndSort = () => {
      let filtered = discounts;

      if (search) {
        const searchLower = search.toLowerCase();
        filtered = discounts.filter(discount =>
          discount.discountPublicId.toLowerCase().includes(searchLower) ||
          discount.name.toLowerCase().includes(searchLower)
        );
      }

      const sorted = [...filtered].sort((a, b) => {
        let aValue = a[sortColumn]?.toString().toLowerCase();
        let bValue = b[sortColumn]?.toString().toLowerCase();

        if (sortOrder === 'asc') {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });

      setFilteredDiscounts(sorted);
    };

    applyFilterAndSort();
  }, [search, discounts, sortColumn, sortOrder]);

  const handleGetDiscounts = async () => {
    try {
      const accessToken = await getAccessToken();
      const newDiscounts = await getAllDiscounts(accessToken);

      setDiscounts(newDiscounts);  
      setFilteredDiscounts(newDiscounts);  
      setDiscountsLoaded(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDeleteDiscount = async (discountPublicId) => {
    const toastId = toast.loading(t("toast.deleting", { entity: t("entity.discount") }));
    try {
      const accessToken = await getAccessToken();
      await deleteDiscount(discountPublicId, accessToken);
      const newDiscounts = await getAllDiscounts(accessToken);
      setDiscounts(newDiscounts);  
      toast.success(t("toast.deleted", { entity: t("entity.discount") }), { id: toastId });
    } catch (error) {
      toast.error(t("toast.failedDelete", { entity: t("entity.discount") }), { id: toastId });
      console.error(error);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn === column) {
      return sortOrder === 'asc' ? faArrowDownShortWide : faArrowDownWideShort;
    }
    return null;
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">

            <div className="absolute inset-0 pointer-events-none">
              <LoadingTablePage title={t("entity.discounts")} loading={!discountsLoaded} />
            </div>

            <div className={`${!discountsLoaded ? "loading" : ""} page-content`}>
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">{t("entity.discounts")}</h2>
                <button className="btn btn-primary" onClick={() => { navigate(`/discount/create`) }}>
                  <FontAwesomeIcon icon={faPlus} /> {t("pages.createDiscount")}
                </button>
              </div>
              <div className="rounded-md shadow-md w-full p-4">
                <div className="flex w-full justify-end">
                  <div className="mb-2">
                    <InputField icon={faSearch} placeholder={t("search.discount")} value={search} onChange={(v) => { setSearch(v.target.value) }} />
                  </div>
                </div>
                <div className="overflow-x-auto pb-[250px] mb-[-250px]">
                  <table className="table-default w-full">
                    <thead>
                      <tr>
                        <th className="text-center cursor-pointer" onClick={() => handleSort('discountPublicId')}>
                          {t("table.discountId")} {getSortIcon('discountPublicId') && <FontAwesomeIcon icon={getSortIcon('discountPublicId')} className="w-4 pl-2" />}
                        </th>
                        <th className="text-center cursor-pointer" onClick={() => handleSort('name')}>
                          {t("common.name")} {getSortIcon('name') && <FontAwesomeIcon icon={getSortIcon('name')} className="w-4 pl-2" />}
                        </th>
                        <th className="text-center cursor-pointer" onClick={() => handleSort('description')}>
                          {t("common.description")} {getSortIcon('description') && <FontAwesomeIcon icon={getSortIcon('description')} className="w-4 pl-2" />}
                        </th>
                        <th className="text-center cursor-pointer" onClick={() => handleSort('discountValueType')}>
                          {t("common.type")} {getSortIcon('discountValueType') && <FontAwesomeIcon icon={getSortIcon('discountValueType')} className="w-4 pl-2" />}
                        </th>
                        <th className="text-center cursor-pointer" onClick={() => handleSort('isCalculatedPerChargingSite')}>
                          {t("table.scope")} {getSortIcon('isCalculatedPerChargingSite') && <FontAwesomeIcon icon={getSortIcon('isCalculatedPerChargingSite')} className="w-4 pl-2" />}
                        </th>
                        <th className="text-center cursor-pointer" onClick={() => handleSort('minKwh')}>
                          {t("table.minKwh")} {getSortIcon('minKwh') && <FontAwesomeIcon icon={getSortIcon('minKwh')} className="w-4 pl-2" />}
                        </th>
                        <th className="text-center cursor-pointer" onClick={() => handleSort('maxKwh')}>
                          {t("table.maxKwh")} {getSortIcon('maxKwh') && <FontAwesomeIcon icon={getSortIcon('maxKwh')} className="w-4 pl-2" />}
                        </th>
                        <th className="text-center cursor-pointer" onClick={() => handleSort('discountValue')}>
                          {t("common.amount")} {getSortIcon('discountValue') && <FontAwesomeIcon icon={getSortIcon('discountValue')} className="w-4 pl-2" />}
                        </th>
                        <th className="text-center cursor-pointer" onClick={() => handleSort('startDateTimeUtc')}>
                          {t("common.start")} {getSortIcon('startDateTimeUtc') && <FontAwesomeIcon icon={getSortIcon('startDateTimeUtc')} className="w-4 pl-2" />}
                        </th>
                        <th className="text-center cursor-pointer" onClick={() => handleSort('endDateTimeUtc')}>
                          {t("common.end")} {getSortIcon('endDateTimeUtc') && <FontAwesomeIcon icon={getSortIcon('endDateTimeUtc')} className="w-4 pl-2" />}
                        </th>
                      
                        <th className="text-center"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredDiscounts.map((discount, index) => (
                        <tr key={index}>
                          <td className="cursor-pointer" onClick={() => { navigate(`/discount/${discount.discountPublicId}`) }}>{discount.discountPublicId}</td>
                          <td  className="cursor-pointer" onClick={() => { navigate(`/discount/${discount.discountPublicId}`) }}>{discount.name}</td>
                          <td  className="cursor-pointer" onClick={() => { navigate(`/discount/${discount.discountPublicId}`) }}>{discount.description}</td>
                          <td  className="cursor-pointer" onClick={() => { navigate(`/discount/${discount.discountPublicId}`) }}>{t(`partner.priceValueType.${discount.discountValueType}`, { defaultValue: discount.discountValueType })}</td>
                          <td  className="cursor-pointer" onClick={() => { navigate(`/discount/${discount.discountPublicId}`) }}>{discount.isCalculatedPerChargingSite ? t("entity.site") : t("common.global") }</td>
                          <td  className="cursor-pointer" onClick={() => { navigate(`/discount/${discount.discountPublicId}`) }}>{discount.minKwh}</td>
                          <td  className="cursor-pointer" onClick={() => { navigate(`/discount/${discount.discountPublicId}`) }}>{discount.maxKwh}</td>
                          <td  className="cursor-pointer" onClick={() => { navigate(`/discount/${discount.discountPublicId}`) }}>{discount.discountValueType === "Fixed" ? `${discount.discountValue} kr/kWh`: `${discount.discountValue}%`}</td>
                          <td  className="cursor-pointer" onClick={() => { navigate(`/discount/${discount.discountPublicId}`) }}>{format(new Date(discount.startDateTimeUtc), 'yyyy-MM-dd HH:mm')}</td>
                          <td  className="cursor-pointer" onClick={() => { navigate(`/discount/${discount.discountPublicId}`) }}>{discount.endDateTimeUtc ? format(new Date(discount.endDateTimeUtc), 'yyyy-MM-dd HH:mm') : '-'}</td>

                          <td className="text-center flex justify-end">
                            <CustomDropdown isRight={true}
                              ContentComponent={() => {
                                return (
                                  <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                                    <button
                                      className="block px-4 py-2 text-sm text-black hover:bg-mute-200 w-full text-left rounded-lg"
                                      onClick={() => { navigate(`/discount/${discount.discountPublicId}`) }}
                                    >
                                      <FontAwesomeIcon icon={faPenToSquare} /> {t("common.edit")}
                                    </button>
                                    <button
                                      className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                                      onClick={() => { setConfirmState({ isOpen: true, title: t("confirm.deleteEntity", { entity: t("entity.discount"), name: discount.name }), confirm: () => { handleDeleteDiscount(discount.discountPublicId) } }) }}
                                    >
                                      <FontAwesomeIcon icon={faCircleMinus} /> {t("common.delete")}
                                    </button>
                                  </div>
                                );
                              }}>
                              <div className="w-8 h-6 flex justify-end bg-red items-center cursor-pointer">
                                <FontAwesomeIcon icon={faEllipsis} />
                              </div>
                            </CustomDropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => { setConfirmState((s) => ({ ...s, isOpen: false })) }}
        onConfirm={() => { setConfirmState((s) => ({ ...s, isOpen: false })); confirmState.confirm() }}
      />
    </>
  );
};

export default DiscountsPage;
